import React from 'react'
import { observer } from 'mobx-react-lite'
import { isSameDay } from 'date-fns/isSameDay'

import Badge from '@mui/material/Badge'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'

import {
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
} from '../../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../../core/constants'
import { RecurrenceStatus, RecurrenceType } from '../../../core'

import { IncomeBadge, MissedBadge, PaidBadge, UpcomingExpenseBadge } from '../RecurrenceBadges'

export const RecurrenceDay = observer(
  ({ day, outsideCurrentMonth, ...other }: PickersDayProps<Date>) => {
    const { recurring: copy } = useGlobalCopyStore()
    const { selectedAccountGuids } = useGlobalUiStore()
    const { recurrences, setSelectedDay } = useRecurringTransactionsStore()

    const filteredRecurrences = React.useMemo(
      () =>
        recurrences.filter((recurrence) => selectedAccountGuids.includes(recurrence.accountGuid)),
      [selectedAccountGuids, recurrences],
    )

    const dayRecurrences = filteredRecurrences.filter((a) =>
      isSameDay(a.occurredOnDate || a.expectedDate, day),
    )
    const hasUpcoming = dayRecurrences.some(
      (a) => a.type !== RecurrenceType.Income && a.status === RecurrenceStatus.Upcoming,
    )
    const hasMissed = dayRecurrences.some(
      (a) => a.type !== RecurrenceType.Income && a.status === RecurrenceStatus.Missed,
    )
    const hasPaid = dayRecurrences.some(
      (a) => a.type !== RecurrenceType.Income && a.status === RecurrenceStatus.Paid,
    )
    const hasIncome = dayRecurrences.some((a) => a.type === RecurrenceType.Income)

    const totalBadges =
      (hasUpcoming ? 1 : 0) + (hasMissed ? 1 : 0) + (hasPaid ? 1 : 0) + (hasIncome ? 1 : 0)

    const statusParts = [
      hasPaid && copy.paid,
      hasMissed && copy.missed,
      hasUpcoming && copy.upcoming,
      hasIncome && copy.income,
    ].filter(Boolean) as string[]

    const ariaLabel =
      statusParts.length > 0
        ? `${formatDate(day, DATE_FORMATS_INTL.MONTH_DAY_YEAR)}, ${statusParts.join(', ')}`
        : formatDate(day, DATE_FORMATS_INTL.MONTH_DAY_YEAR)

    return (
      <Badge
        badgeContent={
          dayRecurrences.length > 0 && (
            <>
              {hasMissed && <MissedBadge isOffset={true} />}
              {hasPaid && <PaidBadge isOffset={true} />}
              {hasUpcoming && <UpcomingExpenseBadge isOffset={true} />}
              {hasIncome && <IncomeBadge isOffset={true} />}
            </>
          )
        }
        overlap="circular"
        sx={{
          '& .MuiBadge-badge': {
            padding: 0,
            marginRight: `${(totalBadges - 1) * 6 + 1}px`,
            flexWrap: 'nowrap',
          },
        }}
      >
        <PickersDay
          aria-label={ariaLabel}
          day={day}
          onClick={() => setSelectedDay(day)}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            '&.MuiPickersDay-root': {
              ...(outsideCurrentMonth && {
                color: 'text.disabled',
              }),
              ...(other.today && {
                color: 'primary.main',
              }),
            },
          }}
          {...other}
        />
      </Badge>
    )
  },
)
